import React, { useCallback } from 'react';
import type { ScheduleItem, TableColumn, ColumnFilters, ColumnFilterValue } from '@app-types';
import { UserPermissions } from '@contexts/AuthContext';
import { SchedulerCellRenderer } from './SchedulerTableCells';
import { RZTable, ScheduleTableBody } from '@components/common/table';
import EmptyState from '@components/common/EmptyState';
import type { RenderCellParams } from '@components/common/table/ScheduleTableBody';
import { useTableExpand } from '@hooks/useTableExpand';

interface SchedulerTableProps {
  scheduleItems: ScheduleItem[];
  columns: TableColumn[];
  permissions: UserPermissions;
  selectedItems: Set<number>;
  onToggleSelection: (itemId: number | null, contractId: number, item?: ScheduleItem) => void;
  onAssignRole: (item: ScheduleItem, contractId: number, role: 'owner' | 'manager' | 'assessor') => void;
  onScheduleDate: (item: ScheduleItem) => void;
  onRemoveFromGroup: (groupId: number, contractId: number) => void;
  onAddToGroup: (item: ScheduleItem) => void;
  canScheduleMeeting: (item: ScheduleItem) => boolean;
  navigate: (path: string) => void;
  onColumnSort: (columnKey: string) => void;
  sortBy: string;
  sortOrder: string;
  getSortIndicator: (columnKey: string) => string | null;
  isColumnSorted: (columnKey: string) => boolean;
  currentStateFilter?: string;
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange?: (pageSize: number) => void;
    itemName?: string;
    pageSizeOptions?: number[];
  };
  columnFilters?: {
    values: ColumnFilters;
    onChange: (columnKey: string, value: ColumnFilterValue | null) => void;
    onClearAll: () => void;
    hasActiveFilters: boolean;
  };
  isEmpty?: boolean;
}

export const SchedulerTable: React.FC<SchedulerTableProps> = ({
  scheduleItems,
  columns,
  permissions,
  selectedItems,
  onToggleSelection,
  onAssignRole,
  onScheduleDate,
  onRemoveFromGroup,
  onAddToGroup,
  canScheduleMeeting,
  navigate,
  onColumnSort,
  sortBy,
  sortOrder,
  getSortIndicator,
  isColumnSorted,
  currentStateFilter,
  pagination,
  columnFilters,
  isEmpty = false
}) => {
  // Use generic expand/collapse hook
  const { toggleExpand, isExpanded } = useTableExpand();

  const getSelectionId = (item: ScheduleItem) => {
    return item.id !== null ? item.id : item.contracts[0].id;
  };

  const isItemSelected = (item: ScheduleItem) => {
    const selectionId = getSelectionId(item);
    return selectedItems.has(selectionId);
  };

  // Render cell with all necessary props
  const renderCell = useCallback(
    (params: RenderCellParams) => {
      const { column, item, contract, isGroupRow, isChildRow, isSelected = false, isGroupExpanded = false } = params;

      // Disable checkboxes when filtering by pending_data_enrichment OR when meeting is scheduled/rescheduled
      const isCheckboxDisabled =
        currentStateFilter === 'pending_data_enrichment' ||
        item.meeting?.status === 'scheduled' ||
        item.meeting?.status === 'rescheduled';

      return (
        <SchedulerCellRenderer
          column={column}
          scheduleItem={item}
          contract={contract}
          isGroupRow={isGroupRow}
          isChildRow={isChildRow}
          permissions={permissions}
          onAssignRole={onAssignRole}
          onScheduleDate={onScheduleDate}
          canScheduleMeeting={canScheduleMeeting}
          navigate={navigate}
          isSelected={isSelected}
          isExpanded={isGroupExpanded}
          onToggleSelection={onToggleSelection}
          onToggleExpand={toggleExpand}
          onAddToGroup={onAddToGroup}
          onRemoveFromGroup={onRemoveFromGroup}
          isCheckboxDisabled={isCheckboxDisabled}
        />
      );
    },
    [
      permissions,
      onAssignRole,
      onScheduleDate,
      canScheduleMeeting,
      navigate,
      onToggleSelection,
      toggleExpand,
      onAddToGroup,
      onRemoveFromGroup,
      currentStateFilter
    ]
  );

  // Add extra column for sticky Clear button when filters are active
  const hasActiveClearButton = columnFilters?.hasActiveFilters;
  const totalColumns = hasActiveClearButton ? columns.length + 1 : columns.length;

  return (
    <RZTable.Container className="scheduler-table" pagination={pagination}>
      <RZTable.Header
        columns={columns}
        sorting={{
          sortConfig: { field: sortBy, direction: sortOrder as 'ASC' | 'DESC' },
          onSort: onColumnSort,
          isSorted: isColumnSorted
        }}
        columnFilters={columnFilters}
      />
      {isEmpty ? (
        <tbody>
          <tr>
            <td colSpan={totalColumns} className="table-empty-cell">
              <EmptyState
                type="default"
                title="No schedule items found"
                description="No schedule items match the current filter criteria."
              />
            </td>
          </tr>
        </tbody>
      ) : (
        <ScheduleTableBody
          scheduleItems={scheduleItems}
          columns={columns}
          isExpanded={isExpanded}
          renderCell={renderCell}
          selectedItems={selectedItems}
          onToggleSelection={onToggleSelection}
          isItemSelected={isItemSelected}
        />
      )}
    </RZTable.Container>
  );
};
